<template>
  <div>
    <div class="flex justify-center">
      <div class="flex h-full w-full flex-col justify-center p-24 md:w-[34rem]">
        <RevStepper
          :active-step="STEP_NAMES.SHIPPING"
          :alternative-text-back="i18n(translations.previousPage)"
          :alternative-text-close="i18n(translations.stepperClose)"
          :alternative-text-completed="i18n(translations.stepperCompleted)"
          :alternative-text-current="i18n(translations.stepperCurrent)"
          :has-close="false"
          :hasBack="hasBackButton"
          :steps
          @back="goBack"
        />
        <div class="flex flex-col justify-start pt-32">
          <span class="body-1-bold">
            {{ i18n(translations.question) }}
          </span>
          <span class="body-1 my-16">
            {{ i18n(translations.description) }}
          </span>
          <FormGenerator
            v-if="formConfig"
            :form-config
            form-id="shipping-form"
            :isLoading
            submit-button-id="shipping-submit"
            :submit-button-label="i18n(translations.button)"
            @form-submit="handleSubmit"
          />
          <template v-else>
            <RevSkeleton
              v-for="index in 3"
              :key="`form-skeleton-${index}`"
              class="mt-16"
              height="106px"
              shape="rectangle"
              width="100%"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed, inject, onMounted, ref, watch } from 'vue'

import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import type { FormValues } from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.types'
import FormGenerator from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevStepper } from '@ds/components/Stepper'

import { useBuybackFunnelOffer } from '~/scopes/buyback/composables/useBuybackFunnelOffer'
import { useResaleStepper } from '~/scopes/buyback/composables/useResaleStepper'
import { generateShippingForm } from '~/scopes/buyback/config/generateShippingForm'
import {
  ERROR_TYPES,
  ROUTE_NAMES,
  STEP_NAMES,
} from '~/scopes/buyback/constants'
import { FUNNEL_ERROR_MESSAGE } from '~/scopes/buyback/pages/constants'
import { CMS } from '~/scopes/cms/routes-names'

import type { ErrorData } from '../components/TheCatcher/useCatcher'

import translations from './Shipping.translations'

const i18n = useI18n()
const isLoading = ref(false)
const tracking = useTracking()
const { query, params } = useRoute()
const logger = useLogger()
const { openErrorToast } = useTheToast()
const $httpFetch = useHttpFetch()

const { offer } = useBuybackFunnelOffer()

const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = {
  message: FUNNEL_ERROR_MESSAGE.BUYBACK_FUNNEL,
  step: 'Shipping',
}

const { steps, goBack, hasBackButton } = useResaleStepper({
  activeStepName: STEP_NAMES.ADDRESS,
  hasShippingStep: offer.value?.shipping.hasChoice,
})

const { data: shippingInfo, error: shippingInfoError } = useAsyncData(() =>
  $httpFetch(
    '/buyback-funnel/api/v1/funnel/:kind/offer/:listingPublicId/shipping',
    {
      pathParams: {
        kind: 'regular',
        listingPublicId: params?.listingId?.toString() || '',
      },
    },
  ),
)

const formConfig = computed(() => {
  if (!shippingInfo.value) return null

  return generateShippingForm(shippingInfo.value)
})

watch(shippingInfoError, () => {
  if (!shippingInfoError.value) return

  logger.error(FUNNEL_ERROR_MESSAGE.GET_SHIPPING_DETAILS, {
    statusCode: shippingInfoError.value.statusCode,
    statusMessage: shippingInfoError.value.message,
    owners: ['bot-squad-circularity-offer-front'],
  })
})

async function handleSubmit(values: FormValues) {
  isLoading.value = true
  try {
    const shippingMode =
      typeof values['shipping-mode'] === 'string'
        ? values['shipping-mode']
        : undefined

    tracking.trackClick({
      zone: 'buyback',
      name: 'shipping_info',
      value: {
        category: params?.id,
        shipping: shippingMode,
      },
    })

    const payload = await $httpFetch('/bm/sourcing/order/new', {
      body: {
        customerAddressId: params?.addressId?.toString(),
        listing_id: params?.listingId?.toString(),
        shippingId: shippingMode,
      },
      method: 'POST',
    })

    tracking.trackBuybackConfirmation({
      buybackType: 'bb_normal',
      buybackOrderId: payload.id,
      commission: payload.estimatedCommission?.amount,
      conversionValue: payload.price,
      conversionCurrency: payload.estimatedCommission?.currency,
    })

    await navigateTo({
      name: ROUTE_NAMES.CONFIRMATION,
      params: { id: payload.id },
      query,
    })
  } catch (shippingError) {
    const error = shippingError as HttpApiError | Error
    logger.error(FUNNEL_ERROR_MESSAGE.POST_ORDER, {
      error,
      owners: ['bot-squad-circularity-offer-front'],
    })

    if (shippingError instanceof HttpApiError) {
      if (shippingError.type === ERROR_TYPES.INVALID_ID_ERROR_TYPE) {
        openErrorToast({ content: i18n(translations.errorUnableToProcess) })
      } else if (shippingError.type === ERROR_TYPES.NOT_FOUND_ERROR_TYPE) {
        openErrorToast({ content: i18n(translations.errorListingNotFound) })
      } else {
        openErrorToast({
          title: i18n(translations.errorTitle),
          content: i18n(translations.errorDescription),
        })
      }
    } else {
      openErrorToast({
        title: i18n(translations.errorTitle),
        content: i18n(translations.errorDescription),
      })
    }
    isLoading.value = false
  }
}

onMounted(async () => {
  if (!offer.value) {
    await navigateTo({
      name: CMS.BUYBACK,
      params: {
        pageName: 'home',
      },
    })
  }
})
</script>
